import { useState, useEffect, useRef } from "react";
import { useLanguage } from "../contexts/LanguageContext";
import { useSelector } from "react-redux";
import { RootState } from "../store";
import { useCreateTaskMutation, useAddTaskToTemplateMutation } from "../store/api/templates";
import { toast } from "sonner";
import { handleApiError } from "../utils/apiErrors";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

interface AddTaskToTemplateModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  templateId: string;
  templateName: string;
}

export default function AddTaskToTemplateModal({
  open,
  onOpenChange,
  templateId,
  templateName,
}: AddTaskToTemplateModalProps) {
  const { t } = useLanguage();
  const dialogContentRef = useRef<HTMLDivElement>(null);
  const currentWorkspace = useSelector((state: RootState) => state.workspace.currentWorkspace);
  const [createTask, { isLoading: isCreatingTask }] = useCreateTaskMutation();
  const [addTaskToTemplate, { isLoading: isAddingTask }] = useAddTaskToTemplateMutation();
  
  const isLoading = isCreatingTask || isAddingTask;
  
  const [taskName, setTaskName] = useState("");
  const [description, setDescription] = useState("");
  const [recurrenceType, setRecurrenceType] = useState<"none" | "daily" | "weekly" | "monthly" | "quarterly" | "semi-annually" | "yearly">("none");

  const handleSave = async () => {
    if (!currentWorkspace) {
      toast.error(t('workspace.noWorkspaceSelected'));
      return;
    }
    
    if (!taskName.trim()) {
      toast.error(t('settings.enterTaskName'));
      return;
    }

    try {
      // Step 1: Create the task
      const newTask = await createTask({
        workspaceId: currentWorkspace.id,
        data: {
          name: taskName.trim(),
          description: description.trim() || undefined,
          recurrence: { type: recurrenceType },
        },
      }).unwrap();

      // Step 2: Add the task to the template
      await addTaskToTemplate({
        workspaceId: currentWorkspace.id,
        templateId: templateId,
        taskId: newTask.id,
      }).unwrap();

      toast.success(t('settings.taskAddedToTemplate') || 'Task added to template');

      // Reset form
      setTaskName("");
      setDescription("");
      setRecurrenceType("none");
      onOpenChange(false);
    } catch (error) {
      handleApiError(error, t('settings.taskAddFailed') || 'Failed to add task');
    }
  };

  const handleCancel = () => {
    // リセット
    setTaskName("");
    setDescription("");
    setRecurrenceType("none");
    onOpenChange(false);
  };

  // Scroll to top when modal opens
  useEffect(() => {
    if (open && dialogContentRef.current) {
      dialogContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent ref={dialogContentRef} className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('settings.addTaskToTemplate')}</DialogTitle>
          <DialogDescription>
            {t('settings.addTaskToTemplateDesc')} - {templateName}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="mb-2">{t('settings.taskNameRequired')}</Label>
            <Input
              value={taskName}
              onChange={(e) => setTaskName(e.target.value)}
              placeholder={t('settings.taskNamePlaceholder')}
            />
          </div>

          <div>
            <Label className="mb-2">{t('tasks.taskSummary')}</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('settings.taskDescPlaceholder')}
              rows={3}
            />
          </div>

          <div>
            <Label className="mb-2">{t('settings.recurrenceSetting')}</Label>
            <Select
              value={recurrenceType}
              onValueChange={(value: "none" | "daily" | "weekly" | "monthly" | "quarterly" | "semi-annually" | "yearly") =>
                setRecurrenceType(value)
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{t('tasks.none')}</SelectItem>
                <SelectItem value="daily">{t('tasks.daily')}</SelectItem>
                <SelectItem value="weekly">{t('tasks.weekly')}</SelectItem>
                <SelectItem value="monthly">{t('tasks.monthly')}</SelectItem>
                <SelectItem value="quarterly">{t('tasks.quarterly')}</SelectItem>
                <SelectItem value="semi-annually">{t('tasks.semiAnnually')}</SelectItem>
                <SelectItem value="yearly">{t('tasks.yearly')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel} disabled={isLoading}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSave} disabled={isLoading}>
            {isLoading ? t('common.saving') : t('common.add')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
